import { useEffect, useCallback, FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTypedTranslations } from '../../../../translation/use-translations';
import PromotionTextKeys from './sportsbook-promotion-carousel-text-keys';
import { trackSportsbookPromotionCarouselClick, trackSportsbookPromotionCarouselImpression } from '@comeon/analytics';
import { isPromotionLink } from './sportsbook-promotion-carousel-types';
import { selectIsAuthenticated } from '../../sportsbook-root-selectors';
import useQueryPromotions from '../../queries/promotions/use-query-promotions';
import { PromotionItemDto, PromotionItemRestriction } from '../../api/sbapi/sbapi-api-model';
import PromotionItemLink from './sportsbook-promotion-carousel-item-link';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import SportsbookCarousel from '../../components/carousel/sportsbook-carousel';
import useResources from '../../../../resource/use-resources';
import { CarouselSkeleton } from './sportsbook-promotion-carousel-item-base';

const carouselName = 'Promotion Carousel';
const skeletons = Array(3)
	.fill(0)
	.map((_, i) => `promotion-skeleton-key-${i}`);

const mapPromotionToTrackingPromotion = (promotion: PromotionItemDto) => {
	if (isPromotionLink(promotion)) {
		return {
			id: promotion.id,
			type: promotion.linkType,
			source: promotion.source
		};
	}

	return {
		id: promotion.id,
		type: promotion.type,
		source: promotion.source,
		sportId: promotion.sportId,
		selectionIds: promotion.selectionIds,
		marketIds: promotion.marketIds
	};
};

/**
 * Query promotions and filter out the ones that are not allowed for the current user (based on restrictions).
 */
const usePromotions = () => {
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const { data = [], isPending } = useQueryPromotions();

	const promotions = useMemo(
		() =>
			data.filter(promotion => {
				const hasRestrictions = promotion.restrictions.length > 0;
				if (!hasRestrictions) {
					return true;
				}

				if (
					(promotion.restrictions.includes(PromotionItemRestriction.AUTHENTICATED_ONLY) && isAuthenticated) ||
					(promotion.restrictions.includes(PromotionItemRestriction.UNAUTHENTICATED_ONLY) && !isAuthenticated)
				) {
					return true;
				}

				return false;
			}),
		[data, isAuthenticated]
	);

	return { promotions, isPending };
};

/**
 * Query resources for promotions.
 * @param promotions Promotions to get resources for
 * @returns resources and loading state
 */
const usePromotionResources = (promotions: PromotionItemDto[]) => {
	const resourceKeys = useMemo(
		() => promotions.map(promotion => promotion.backgroundImageResourceKey).filter(Boolean),
		[promotions]
	);
	const { resources, isLoading: isLoadingResources } = useResources(resourceKeys);

	return { resources, isLoadingResources };
};

/**
 * Hook for tracking carousel impressions and clicks.
 * @param promotions Promotions to track
 * @returns function to handle item click
 */
const useTracking = (promotions: PromotionItemDto[]) => {
	// Track impression
	useEffect(() => {
		if (promotions.length === 0) {
			return;
		}

		trackSportsbookPromotionCarouselImpression(promotions.map(mapPromotionToTrackingPromotion), carouselName);
	}, [promotions]);

	// Track click
	const handleItemClick = useCallback(
		(index: number) => {
			const promotion = promotions[index];
			if (promotion) {
				trackSportsbookPromotionCarouselClick(mapPromotionToTrackingPromotion(promotion), carouselName, index);
			}
		},
		[promotions]
	);

	return { handleItemClick };
};

const PromotionCarouselContainer: FC = () => {
	const { promotions, isPending } = usePromotions();

	const { resources, isLoadingResources } = usePromotionResources(promotions);
	const { translations, isLoadingTranslations } = useTypedTranslations(PromotionTextKeys);

	const isSmall = useSelector(isSmallerSelector);

	const { handleItemClick } = useTracking(promotions);

	// Loading
	if (isPending || isLoadingTranslations || isLoadingResources) {
		return (
			<SportsbookCarousel showArrows={!isSmall} gap={isSmall ? 12 : 20} paddingX={isSmall ? 12 : 0}>
				{skeletons.map(s => (
					<CarouselSkeleton key={s} />
				))}
			</SportsbookCarousel>
		);
	}

	// No data
	if (!isPending && promotions.length === 0) {
		return null;
	}

	// Render as usual
	return (
		<SportsbookCarousel showArrows={!isSmall} gap={isSmall ? 12 : 20} paddingX={isSmall ? 12 : 0}>
			{promotions.map((promotion, index) => {
				if (isPromotionLink(promotion)) {
					return (
						<PromotionItemLink
							key={promotion.id}
							promotion={promotion}
							staticTranslations={translations}
							resources={resources}
							index={index}
							onItemClick={handleItemClick}
						/>
					);
				}

				return null;
			})}
		</SportsbookCarousel>
	);
};

export default PromotionCarouselContainer;
